// src/features/roles/rolesSlice.ts
import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { divisionService } from 'src/data/api/orbit-service/divisionService';
import { ApiRequestStatus, initApiStatus } from 'src/types/ApiRequestStatus';
import {
  Division,
  CreateDivisionRequest,
  UpdateDivisonRequest,
  DeleteDivisionRequest,
  ListDivisionsRequest,
} from 'src/types/orbit/Division';
import { handleError } from 'src/utils/generalUtils';
import {
  addDivisionToBusinessUnit,
  deleteDivisionFromBusinessUnit,
  updateDivisionToBusinessUnit,
} from 'src/store/businessUnit/businessUnitSlice';

export interface DivisionState {
  divisionList: Division[];
  fetcDivisionsApiStatus: ApiRequestStatus;
  createDivisionApiStatus: DivisionApiStatus;
  updateDivisionApiStatus: DivisionApiStatus;
  deleteDivisionApiStatus: DivisionApiStatus;
}

interface DivisionApiStatus extends ApiRequestStatus {
  divisionName?: string;
}

const initialState: DivisionState = {
  divisionList: [],
  fetcDivisionsApiStatus: initApiStatus,
  createDivisionApiStatus: initApiStatus,
  updateDivisionApiStatus: initApiStatus,
  deleteDivisionApiStatus: initApiStatus,
};

export const fetchDivisionByBusinessUnitApiThunk = createAsyncThunk(
  'divisions/fetchdivisions',
  async (data: ListDivisionsRequest, { rejectWithValue }) => {
    try {
      const response = await divisionService.getAllByBusinessUnit(data);
      return response.divisions;
    } catch (error: any) {
      return rejectWithValue(handleError(error));
    }
  },
);

export const createDivisionApiThunk = createAsyncThunk(
  'divisions/createDivision',
  async (data: CreateDivisionRequest, { rejectWithValue, dispatch }) => {
    try {
      const response = await divisionService.create(data);
      // Dispatch action to add division to business unit
      dispatch(
        addDivisionToBusinessUnit({
          businessUnitId: response.division.businessUnit,
          division: response.division,
        }),
      );
      return {
        division: response.division,
        message: 'Division created successfully',
      };
    } catch (error: any) {
      return rejectWithValue(handleError(error));
    }
  },
);

export const updateDivisionApiThunk = createAsyncThunk(
  'divisions/updateDivision',
  async (data: UpdateDivisonRequest, { rejectWithValue, dispatch }) => {
    try {
      const response = await divisionService.update(data);

      dispatch(
        updateDivisionToBusinessUnit({
          ...response.division,
          businessUnit: data.businessUnit,
        }),
      );
      return {
        division: response.division,
        message: 'Division updated successfully',
      };
    } catch (error: any) {
      return rejectWithValue(handleError(error));
    }
  },
);

export const deleteDivisionApiThunk = createAsyncThunk(
  'divisions/deleteDivision',
  async (data: DeleteDivisionRequest, { rejectWithValue, dispatch }) => {
    try {
      const response = await divisionService.delete(data);

      dispatch(
        deleteDivisionFromBusinessUnit({
          divisionName: response.division.divisionName,
          program: data.programId,
          businessUnit: data.businessUnit,
        }),
      );
      return {
        divisionName: response.division.divisionName,
        message: 'Division deleted successfully',
      };
    } catch (error: any) {
      return rejectWithValue(handleError(error));
    }
  },
);

const divisionSlice = createSlice({
  name: 'divisions',
  initialState,
  reducers: {
    clearDivisionMessages: (state) => {
      state.createDivisionApiStatus.errorMessage = '';
      state.createDivisionApiStatus.successMessage = '';

      state.updateDivisionApiStatus.errorMessage = '';
      state.updateDivisionApiStatus.successMessage = '';

      state.deleteDivisionApiStatus.errorMessage = '';
      state.deleteDivisionApiStatus.successMessage = '';
    },
    removeFromDivisionList: (state, action: PayloadAction<string>) => {
      state.divisionList = state.divisionList.filter(
        (division) => division.divisionName !== action.payload,
      );
    },
    updateDivision: (state, action: PayloadAction<any>) => {
      state.divisionList = state.divisionList.map((division) =>
        division.divisionName === action.payload.division.divisionId
          ? { ...division, ...action.payload.division }
          : division,
      );
    },
    addToDivisionList: (state, action: PayloadAction<any>) => {
      state.divisionList.push(action.payload);
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchDivisionByBusinessUnitApiThunk.pending, (state) => {
        state.divisionList = [];
        state.fetcDivisionsApiStatus.isLoading = true;
        state.fetcDivisionsApiStatus.errorMessage = null;
      })
      .addCase(fetchDivisionByBusinessUnitApiThunk.fulfilled, (state, action) => {
        state.fetcDivisionsApiStatus.isLoading = false;
        state.divisionList = action.payload;
      })
      .addCase(fetchDivisionByBusinessUnitApiThunk.rejected, (state, action) => {
        state.fetcDivisionsApiStatus.isLoading = false;
        state.fetcDivisionsApiStatus.errorMessage = action.payload as string;
      })

      // Handle create division actions
      .addCase(createDivisionApiThunk.pending, (state, action) => {
        state.createDivisionApiStatus.isLoading = true;
        state.createDivisionApiStatus.errorMessage = null;
        state.createDivisionApiStatus.successMessage = null;
        state.createDivisionApiStatus.isSuccess = false;
      })
      .addCase(createDivisionApiThunk.fulfilled, (state, action) => {
        state.createDivisionApiStatus.isLoading = false;
        state.createDivisionApiStatus.isSuccess = true;
        state.createDivisionApiStatus.successMessage = action.payload.message;
      })
      .addCase(createDivisionApiThunk.rejected, (state, action) => {
        state.createDivisionApiStatus.isLoading = false;
        state.createDivisionApiStatus.isSuccess = false;
        state.createDivisionApiStatus.errorMessage = action.payload as string;
        state.createDivisionApiStatus.successMessage = null;
      })

      // Handle update division actions
      .addCase(updateDivisionApiThunk.pending, (state, action) => {
        state.updateDivisionApiStatus.divisionName = action.meta.arg.divisionName;
        state.updateDivisionApiStatus.isLoading = true;
        state.updateDivisionApiStatus.errorMessage = null;
        state.updateDivisionApiStatus.successMessage = null;
      })
      .addCase(updateDivisionApiThunk.fulfilled, (state, action) => {
        state.updateDivisionApiStatus.isLoading = false;
        state.updateDivisionApiStatus.isSuccess = true;
        state.updateDivisionApiStatus.successMessage = action.payload.message;
      })
      .addCase(updateDivisionApiThunk.rejected, (state, action) => {
        state.updateDivisionApiStatus.isLoading = false;
        state.updateDivisionApiStatus.isSuccess = false;
        state.updateDivisionApiStatus.errorMessage = action.payload as string;
        state.updateDivisionApiStatus.successMessage = null;
      })

      // Handle delete division actions
      .addCase(deleteDivisionApiThunk.pending, (state, action) => {
        state.deleteDivisionApiStatus.divisionName = action.meta.arg.divisionName;
        state.deleteDivisionApiStatus.isLoading = true;
        state.deleteDivisionApiStatus.errorMessage = null;
        state.deleteDivisionApiStatus.successMessage = null;
      })
      .addCase(deleteDivisionApiThunk.fulfilled, (state, action) => {
        state.deleteDivisionApiStatus.isLoading = false;
        state.deleteDivisionApiStatus.isSuccess = true;
        state.deleteDivisionApiStatus.successMessage = action.payload.message;
      })
      .addCase(deleteDivisionApiThunk.rejected, (state, action) => {
        state.deleteDivisionApiStatus.isLoading = false;
        state.deleteDivisionApiStatus.isSuccess = false;
        state.deleteDivisionApiStatus.errorMessage = action.payload as string;
        state.deleteDivisionApiStatus.successMessage = null;
      });
  },
});

export const {
  removeFromDivisionList,
  updateDivision,
  addToDivisionList,
  clearDivisionMessages,
} = divisionSlice.actions;
export default divisionSlice.reducer;
